<template>
    <div class="calories-calculator">
        <h1 class="heading is-size-4 has-text-centered">Fittaste Kalorienrechner</h1>
        <form v-on:submit.prevent="ready = true">
            <div id="gender-info" class="form-section">
                <h2 class="heading is-size-6">
                    <b-icon icon="gender-male-female"></b-icon>
                    Geschlecht
                </h2>
                <b-field>
                    <b-radio-button v-model="gender" name="gender" :native-value="-161" required>
                        <b-icon icon="gender-female"></b-icon>
                        <span>Weiblich</span>
                    </b-radio-button>

                    <b-radio-button v-model="gender" name="gender" :native-value="5" required>
                        <b-icon icon="gender-male"></b-icon>
                        <span>Männlich</span>
                    </b-radio-button>
                </b-field>
            </div>

            <div id="personal-info" class="form-section">
                <h2 class="heading is-size-6">
                    <b-icon icon="account"></b-icon>
                    Persönliche Informationen
                </h2>
                <div class="columns is-mobile">
                    <div class="column">
                        <b-field label="Alter">
                            <b-input v-model.number="age" @input="age = age === '' ? null : age" type="number"
                                     placeholder="25" required min="18" max="100"></b-input>
                        </b-field>
                    </div>
                    <div class="column">
                        <b-field label="Körpergröße">
                            <b-input v-model.number="height" @input="height = height === '' ? null : height"
                                     type="number" placeholder="180cm" required min="140" max="250"></b-input>
                        </b-field>
                    </div>
                    <div class="column">
                        <b-field label="Gewicht">
                            <b-input v-model.number="weight" @input="weight = weight === '' ? null : weight"
                                     type="number" placeholder="75 Kg" required min="40" max="300"></b-input>
                        </b-field>
                    </div>
                </div>
            </div>

            <div id="activity-and-training-info" class="columns form-section">
                <div class="column">
                    <h2 class="heading is-size-6">
                        <b-icon icon="bike"></b-icon>
                        Körperliche Aktivität
                    </h2>
                    <div class="field">
                        <b-radio v-model="activity"
                                 :native-value="1.20" required>
                            kaum aktiv
                            <b-tooltip multilined
                                       label="Trainiert selten bis garnicht, ist im Beruf ausschließlich sitzend tätig">
                                <b-icon icon="information" required></b-icon>
                            </b-tooltip>
                        </b-radio>
                    </div>
                    <div class="field">
                        <b-radio v-model="activity"
                                 :native-value="1.37" required>
                            leicht aktiv
                            <b-tooltip multilined
                                       label="Wenig Training (1 bis 2 Tage pro Woche), im Job meist sitzend tätig">
                                <b-icon icon="information"></b-icon>
                            </b-tooltip>
                        </b-radio>
                    </div>
                    <div class="field">
                        <b-radio v-model="activity"
                                 :native-value="1.55" required>
                            aktiv
                            <b-tooltip multilined
                                       label="Regelmäßig Sport (3 bis 5 Tage die Woche) oder leichte körperliche Arbeit">
                                <b-icon icon="information"></b-icon>
                            </b-tooltip>
                        </b-radio>
                    </div>
                    <div class="field">
                        <b-radio v-model="activity"
                                 :native-value="1.72" required>
                            sehr aktiv
                            <b-tooltip multilined
                                       required
                                       label="Fast jeden Tag Training (6 bis 7 Tage die Woche) oder körperliche Arbeit">
                                <b-icon icon="information"></b-icon>
                            </b-tooltip>
                        </b-radio>
                    </div>
                    <div class="field">
                        <b-radio v-model="activity"
                                 :native-value="1.90" required>
                            profi Sportler
                            <b-tooltip multilined
                                       label="Zwei mal täglich Sport oder ein besonders körperlich anstrengender Job">
                                <b-icon icon="information"></b-icon>
                            </b-tooltip>
                        </b-radio>
                    </div>
                </div>
                <div class="column">
                    <h2 class="heading is-size-6">
                        <b-icon icon="dumbbell"></b-icon>
                        Trainingsziel
                    </h2>
                    <div class="field">
                        <b-radio v-model="goal"
                                 :native-value="-300" required>
                            Abnehmen
                        </b-radio>
                    </div>
                    <div class="field">
                        <b-radio v-model="goal"
                                 :native-value="0" required>
                            Gewicht halten
                        </b-radio>
                    </div>
                    <div class="field">
                        <b-radio v-model="goal"
                                 :native-value="300" required>
                            Muskelaufbau
                        </b-radio>
                    </div>
                </div>
            </div>

            <b-button type="is-primary" size="is-medium" native-type="submit" class="is-fullwidth">Jetzt Kalorienbedarf berechnen</b-button>
        </form>

        <hr>

        <transition name="fade">
            <div class="result" v-if="ready">
                <h2 class="heading is-size-4 has-text-centered">
                    Dein Ergebnis
                </h2>

                <div class="columns">
                    <div class="column has-text-centered">
                        <div>
                            <p class="heading is-size-6">
                                Dein Kalorienbedarf
                                <b-tooltip
                                        label="Empfohlene Kalorienmenge, um dein persönliches Ziel zu erreichen."
                                        multilined>
                                    <b-icon icon="information"></b-icon>
                                </b-tooltip>
                            </p>
                            <p class="title has-text-primary">{{ caloriesNeeded }}</p>
                        </div>
                    </div>
                    <div class="column has-text-centered">
                        <div>
                            <p class="heading is-size-6">
                                Dein Grundumsatz
                                <b-tooltip
                                        label="Dein Kalorienbedarf im absoluten Ruhezustand."
                                        multilined>
                                    <b-icon icon="information"></b-icon>
                                </b-tooltip>
                            </p>
                            <p class="title">{{ basalCalories }}</p>
                        </div>
                    </div>
                </div>

                <hr>

                <h2 class="heading is-size-4 has-text-centered">
                    Makronährstoffe
                </h2>

                <div class="columns">
                    <div class="column has-text-centered">
                        <div>
                            <p class="heading is-size-6">
                                Kohlenhydrate
                            </p>
                            <p class="title">{{ carbs }}g</p>
                        </div>
                    </div>
                    <div class="column has-text-centered">
                        <div>
                            <p class="heading is-size-6">
                                Eiweiß
                            </p>
                            <p class="title">{{ protein }}g</p>
                        </div>
                    </div>
                    <div class="column has-text-centered">
                        <div>
                            <p class="heading is-size-6">
                                Fett
                            </p>
                            <p class="title">{{ fat }}g</p>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    export default {
        name: 'CaloriesCalculator',
        data() {
            return {
                gender: null,
                age: null,
                weight: null,
                height: null,
                goal: null,
                activity: null,
                ready: false
            }
        },
        mounted() {
            this.updateIframe();
        },
        computed: {
            basalCalories() {
                if (this.weight != null && this.height != null && this.age != null && this.gender != null) {
                    return Math.round(this.weight * 10 + this.height * 6.25 - this.age * 5 + this.gender);
                }
                return null;
            },
            dailyNeeds() {
                if (this.basalCalories && this.activity != null) {
                    return Math.round(this.basalCalories * this.activity);
                }
                return null;
            },
            caloriesNeeded() {
                if (this.dailyNeeds && this.goal != null) {
                    return this.dailyNeeds + this.goal;
                }
                return null;
            },
            carbs() {
                if (this.caloriesNeeded != null)
                    return Math.round((this.caloriesNeeded - this.protein * 4.1 - this.fat * 9.3) / 4.1);
                return null
            },
            protein() {
                if (this.caloriesNeeded != null) {
                    let factor;
                    if (this.activity >= 1.55 && this.goal !== 0) {
                        factor = 2.2;
                    } else if (this.activity >= 1.55 && this.goal === 0) {
                        factor = 2;
                    } else {
                        factor = 1.8;
                    }
                    return Math.round(factor * this.weight);
                }
                return null
            },
            fat() {
                if (this.caloriesNeeded != null) {
                    let factor;
                    if (this.goal >= 0) {
                        factor = 1;
                    } else {
                        factor = 0.6;
                    }
                    return Math.round(factor * this.weight);
                }
                return null
            }
        },
        methods: {
            updateIframe() {
                window.parent.postMessage(document.body.scrollHeight, "*");
            }
        },
        watch: {
            ready: function () {
                this.$nextTick(function() {
                    this.updateIframe();
                });
            }
        }
    }
</script>

<style lang="scss">
    @import "../styles/style";
</style>
